
import React, { useEffect } from "react";
import _ from "lodash";

import { useDispatch, useSelector } from "react-redux";

import { Global } from "@emotion/react";

import { Modal } from "semantic-ui-react";

import { Button, Icon } from "kit/ui";

import type { AppDispatch, RootState } from "src/store/configureStore";

import { yes, no, cancel } from "./Confirm.slice";
import { useGetConfirmResolver, useSetConfirmResolver } from "./Confirm.context";

import * as styles from "./Confirm.styles";

import closeModal from "../../assets/svg/close-modal.svg";


const ICONS_BY_SUB_TYPE = {
  success: "check",
  error: "error",
  warning: "warning",
  info: "info"
} as const;

export type SubType = keyof typeof ICONS_BY_SUB_TYPE;

function Confirm() {

  const dispatch = useDispatch<AppDispatch>();
  const confirmState = useSelector((state: RootState) => state.confirm);
  const getResolver = useGetConfirmResolver();
  const setResolver = useSetConfirmResolver();

  const {
    id,
    buttonNo,
    buttonYes,
    subType,
    title,
    icon,
    content,
    description,
    showButtonNo,
    showCloseIcon = false
  } = confirmState || {};

  const handleYes = () => {
    dispatch(yes());
    const resolver = getResolver();
    if (resolver) {
      resolver("yes");
      setResolver(null);
    }
  };
  const handleNo = () => {
    dispatch(no());
    const resolver = getResolver();
    if (resolver) {
      resolver("no");
      setResolver(null);
    }
  };
  const handleCancel = () => {
    dispatch(cancel());
    const resolver = getResolver();
    if (resolver) {
      resolver("cancel");
      setResolver(null);
    }
  };

  const isOpen = !_.isEmpty(confirmState);

  useEffect(() => {
    if (isOpen) return;
    const resolver = getResolver();
    if (resolver) {
      resolver("cancel");
      setResolver(null);
    }
  }, [ isOpen, getResolver, setResolver ]);

  const iconName =
    icon ?
      `myk ${icon}` :
      subType && ICONS_BY_SUB_TYPE[subType] ?
        `myk ${ICONS_BY_SUB_TYPE[subType]}` :
        undefined;

  return (
    <>
      <Global styles={styles.globalStyles} />
      <Modal
        id={`confirm-${id}`}
        size="small"
        closeOnDocumentClick={false}
        closeOnDimmerClick={false}
        closeOnEscape={false}
        closeIcon={
          showCloseIcon &&
          <img src={closeModal} css={styles.closeIcon} alt="Close Modal Icon" />
        }
        open={isOpen}
        onClose={handleCancel}
        css={[
          styles.base,
          subType && styles.subTypes[subType]
        ]}>
        <Modal.Content>
          {!!iconName && (
            <div
              className="confirm-icon"
              css={styles.icon}>
              <Icon name={iconName} />
            </div>
          )}
          {title && (
            <div
              className="confirm-title"
              css={styles.title}
              dangerouslySetInnerHTML={{
                __html: title
              }} />
          )}
          {!!content && (
            <div
              className="confirm-content"
              css={styles.content}
              dangerouslySetInnerHTML={{
                __html: content
              }} />
          )}
          {!!description && (
            <div
              className="confirm-desc"
              css={styles.description}
              dangerouslySetInnerHTML={{
                __html: description
              }} />
          )}
          <div
            className="confirm-buttons"
            css={styles.actions}>
            {showButtonNo && (
              <Button
                className="confirm-button-no"
                fluid
                size="small"
                outline
                css={styles.buttonNo}
                onClick={handleNo}>
                {_.defaultTo(buttonNo, "No")}
              </Button>
            )}
            <Button
              className="confirm-button-yes"
              outline={!showButtonNo}
              fluid={showButtonNo}
              theme={showButtonNo ? "orange" : "primary"}
              size="small"
              css={[
                !buttonYes && {
                  paddingRight: 70,
                  paddingLeft: 70
                },
                styles.buttonYes
              ]}
              onClick={handleYes}>
              {_.defaultTo(buttonYes, "Si")}
            </Button>
          </div>
        </Modal.Content>
      </Modal>
    </>
  );
}

export default React.memo(Confirm);

